import type { StageQuestion } from '../../data/stageConfigs';
import { haptic } from '../lib/haptic';

interface QuestionInputProps {
  question: StageQuestion;
  value: string;
  onChange: (v: string) => void;
}

/**
 * Renders the right widget for each StageQuestion.type value from
 * data/stageConfigs.ts. Styled for dark + light themes.
 */
export default function QuestionInput({
  question,
  value,
  onChange,
}: QuestionInputProps) {
  switch (question.type) {
    case 'freeText':
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder}
          className="w-full rounded-2xl border-2 border-gray-200 bg-white px-4 py-3 font-gill text-base text-gray-900 placeholder:text-gray-400 focus:border-amber focus:outline-none focus:ring-0 dark:border-white/10 dark:bg-base dark:text-ink dark:placeholder:text-ink/40"
        />
      );

    case 'textArea':
      return (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder}
          rows={4}
          className="w-full resize-none rounded-2xl border-2 border-gray-200 bg-white px-4 py-3 font-gill text-base text-gray-900 placeholder:text-gray-400 focus:border-amber focus:outline-none focus:ring-0 dark:border-white/10 dark:bg-base dark:text-ink dark:placeholder:text-ink/40"
        />
      );

    case 'multipleChoice':
      return (
        <div className="grid gap-3">
          {(question.options ?? []).map((opt) => {
            const selected = value === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  haptic('pop');
                  onChange(opt);
                }}
                className={`flex items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left font-gill text-base transition-[transform,box-shadow] active:scale-[0.98] ${
                  selected
                    ? 'border-amber bg-amber/10 text-gray-900 shadow-glow-amber dark:text-ink'
                    : 'border-gray-200 bg-white text-gray-800 hover:border-amber/40 dark:border-white/10 dark:bg-base dark:text-ink/90'
                }`}
              >
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-gray-300 font-marker text-xs dark:border-white/20">
                  {selected ? '●' : ''}
                </span>
                <span className="flex-1">{opt}</span>
              </button>
            );
          })}
        </div>
      );

    case 'emojiSlider': {
      const stops = question.emojiStops ?? [];
      const current = parseInt(value || question.defaultValue || '0', 10) || 0;
      return (
        <div>
          <div className="flex items-center justify-between gap-1">
            {stops.map((stop, i) => {
              const selected = i === current;
              return (
                <button
                  key={stop.label}
                  type="button"
                  onClick={() => {
                    haptic('pop');
                    onChange(String(i));
                  }}
                  className={`flex flex-1 flex-col items-center gap-1 rounded-2xl border-2 px-2 py-3 transition-transform active:scale-[0.96] ${
                    selected
                      ? 'border-amber bg-amber/10 shadow-glow-amber'
                      : 'border-gray-200 bg-white hover:border-amber/40 dark:border-white/10 dark:bg-base'
                  }`}
                >
                  <span className="text-2xl">{stop.emoji}</span>
                  <span className="font-marker text-[10px] uppercase tracking-wider text-gray-600 dark:text-ink/70">
                    {stop.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    case 'slider': {
      const min = question.sliderMin ?? 0;
      const max = question.sliderMax ?? 100;
      const current = parseInt(value || question.defaultValue || String(min), 10);
      return (
        <div>
          <div className="flex items-center justify-between font-gill text-xs text-gray-600 dark:text-ink/70">
            <span>{question.sliderMinLabel}</span>
            <span>{question.sliderMaxLabel}</span>
          </div>
          <input
            type="range"
            min={min}
            max={max}
            value={current}
            onChange={(e) => onChange(e.target.value)}
            onInput={() => haptic('pop')}
            className="mt-2 w-full accent-amber"
          />
          <div className="mt-1 text-center font-marker text-lg text-amber">
            {current}
          </div>
        </div>
      );
    }

    default:
      return null;
  }
}
